import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../api/api';
import { toast } from '../hooks/use-toast';
import { Envelope, ArrowLeft, Trash, EnvelopeOpen, ArrowBendUpLeft, Archive } from '@phosphor-icons/react';

const STATUS_STYLES = {
  new: 'bg-red-100 text-red-700', read: 'bg-blue-100 text-blue-700',
  replied: 'bg-emerald-100 text-emerald-700', archived: 'bg-neutral-200 text-neutral-500',
};
const TOPIC_STYLES = {
  billing: 'bg-purple-100 text-purple-700 border-purple-300',
  support: 'bg-sky-100 text-sky-700 border-sky-300',
  sales: 'bg-amber-100 text-amber-800 border-amber-300',
  other: 'bg-neutral-100 text-neutral-600 border-neutral-300',
};
const TABS = ['all', 'new', 'read', 'replied', 'archived'];
const TOPICS = ['billing', 'support', 'sales', 'other'];

const AdminContactMessages = () => {
  const navigate = useNavigate();
  const [messages, setMessages] = useState(null);
  const [counts, setCounts] = useState({});
  const [topicCounts, setTopicCounts] = useState({});
  const [tab, setTab] = useState('all');
  const [topicFilter, setTopicFilter] = useState('');
  const [openId, setOpenId] = useState(null);

  const load = useCallback(async () => {
    try {
      const params = {};
      if (tab !== 'all') params.status = tab;
      if (topicFilter) params.topic = topicFilter;
      const r = await axiosInstance.get('/contact/messages', { params });
      setMessages(r.data.messages || []);
      setCounts(r.data.counts || {});
      setTopicCounts(r.data.topic_counts || {});
    } catch (e) {
      if (e.response?.status === 403) navigate('/admin');
      else setMessages([]);
    }
  }, [tab, topicFilter, navigate]);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    try {
      await axiosInstance.patch(`/contact/messages/${id}`, { status });
      toast({ title: `Marked ${status}` });
      load();
    } catch (e) { toast({ title: 'Update failed', description: e.response?.data?.detail, variant: 'destructive' }); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this message?')) return;
    await axiosInstance.delete(`/contact/messages/${id}`);
    toast({ title: 'Message deleted' });
    load();
  };

  const openMessage = (m) => {
    setOpenId(openId === m.id ? null : m.id);
    if (m.status === 'new') setStatus(m.id, 'read');
  };

  return (
    <div className="min-h-screen bg-neutral-100" data-testid="admin-messages-page">
      <div className="bg-neutral-900 border-b-4 border-[#D95800]">
        <div className="max-w-5xl mx-auto px-4 py-6 flex items-center gap-4">
          <button onClick={() => navigate('/admin')} className="text-white hover:text-[#D95800]" data-testid="messages-back-btn">
            <ArrowLeft size={24} weight="bold" />
          </button>
          <div className="w-12 h-12 bg-[#D95800] border-2 border-white flex items-center justify-center">
            <Envelope size={26} weight="fill" className="text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-black text-white uppercase">Message Inbox</h1>
            <p className="text-neutral-400 text-sm">Contact form submissions from visitors</p>
          </div>
          {counts.new > 0 && (
            <span className="ml-auto px-3 py-1.5 bg-red-500 text-white text-sm font-black rounded" data-testid="new-count-badge">
              {counts.new} new
            </span>
          )}
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="flex flex-wrap gap-2 mb-3" data-testid="message-tabs">
          {TABS.map((t) => (
            <button key={t} onClick={() => setTab(t)}
              className={`px-4 h-10 text-sm font-black uppercase border-2 border-black ${tab === t ? 'bg-neutral-900 text-white' : 'bg-white hover:bg-neutral-50'}`}
              data-testid={`tab-${t}`}>
              {t}{t !== 'all' && counts[t] ? ` (${counts[t]})` : ''}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap gap-2 mb-6" data-testid="topic-filters">
          <button onClick={() => setTopicFilter('')}
            className={`px-3 h-8 text-xs font-black uppercase border-2 rounded-full ${!topicFilter ? 'bg-neutral-900 text-white border-black' : 'bg-white border-neutral-300 hover:bg-neutral-50'}`}
            data-testid="topic-all">
            All topics
          </button>
          {TOPICS.map((t) => (
            <button key={t} onClick={() => setTopicFilter(topicFilter === t ? '' : t)}
              className={`px-3 h-8 text-xs font-black uppercase border-2 rounded-full ${topicFilter === t ? 'bg-neutral-900 text-white border-black' : `${TOPIC_STYLES[t]} hover:opacity-80`}`}
              data-testid={`topic-${t}`}>
              {t}{topicCounts[t] ? ` (${topicCounts[t]})` : ''}
            </button>
          ))}
        </div>

        {messages === null && <p className="text-center text-neutral-400 font-bold py-12">Loading…</p>}
        {messages?.length === 0 && (
          <div className="bg-white border-2 border-black p-10 text-center shadow-[4px_4px_0px_#000]" data-testid="no-messages">
            <Envelope size={40} className="mx-auto text-neutral-300 mb-3" />
            <p className="font-black uppercase">No messages{tab !== 'all' ? ` in "${tab}"` : ' yet'}</p>
          </div>
        )}

        <div className="space-y-3">
          {messages?.map((m) => (
            <div key={m.id} className={`bg-white border-2 border-black shadow-[3px_3px_0px_#000] ${m.status === 'new' ? 'border-l-8 border-l-red-500' : ''}`} data-testid={`message-row-${m.id}`}>
              <button onClick={() => openMessage(m)} className="w-full flex flex-wrap items-center gap-3 p-4 text-left" data-testid={`message-toggle-${m.id}`}>
                <span className={`px-2 py-0.5 text-xs font-black rounded uppercase ${STATUS_STYLES[m.status] || ''}`}>{m.status}</span>
                {m.topic && (
                  <span className={`px-2 py-0.5 text-xs font-black rounded-full uppercase border ${TOPIC_STYLES[m.topic] || TOPIC_STYLES.other}`} data-testid={`message-topic-${m.id}`}>
                    {m.topic}
                  </span>
                )}
                <span className="font-black">{m.subject}</span>
                <span className="text-sm text-neutral-500">{m.name} · {m.email}</span>
                <span className="ml-auto text-xs text-neutral-400">{new Date(m.created_at).toLocaleString()}</span>
              </button>
              {openId === m.id && (
                <div className="px-4 pb-4" data-testid={`message-body-${m.id}`}>
                  <p className="text-sm text-neutral-700 whitespace-pre-wrap bg-neutral-50 border border-neutral-200 p-4 mb-3">{m.message}</p>
                  <div className="flex flex-wrap gap-2">
                    <a href={`mailto:${m.email}?subject=Re: ${encodeURIComponent(m.subject)}`}
                      onClick={() => setStatus(m.id, 'replied')}
                      className="h-9 px-3 flex items-center gap-1.5 text-xs font-bold bg-[#D95800] text-white border-2 border-black"
                      data-testid={`reply-btn-${m.id}`}>
                      <ArrowBendUpLeft size={14} weight="bold" /> Reply by Email
                    </a>
                    <button onClick={() => setStatus(m.id, 'replied')} className="h-9 px-3 flex items-center gap-1.5 text-xs font-bold bg-emerald-100 text-emerald-700 border-2 border-emerald-300" data-testid={`mark-replied-${m.id}`}>
                      <EnvelopeOpen size={14} weight="bold" /> Mark Replied
                    </button>
                    <button onClick={() => setStatus(m.id, 'archived')} className="h-9 px-3 flex items-center gap-1.5 text-xs font-bold bg-neutral-100 border-2 border-neutral-300" data-testid={`archive-btn-${m.id}`}>
                      <Archive size={14} weight="bold" /> Archive
                    </button>
                    <button onClick={() => remove(m.id)} className="h-9 px-3 flex items-center gap-1.5 text-xs font-bold bg-red-50 text-red-600 border-2 border-red-300" data-testid={`delete-btn-${m.id}`}>
                      <Trash size={14} weight="bold" /> Delete
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AdminContactMessages;
